/**
 * Ayuda para planificar un año (puro), como lo haría un asesor:
 * - El año de referencia (el anterior) mes a mes por categoría, con sus notas y sus meses EXTRAORDINARIOS
 *   (raros frente a lo normal). Lo pagado de gastos previstos no cuenta como gasto del mes: ya está planificado aparte.
 * - Base recomendada = lo normal SIN los meses extraordinarios (promediarlo todo infla la base; ignorarlo sorprende).
 *   Alternativas: la media con todo, el último trimestre y el patrón mes a mes (para lo que va por temporadas).
 * - Cómo acabará el año: lo real de los meses ya cerrados + lo planificado de los que faltan.
 * - Objetivo de ahorro sugerido: el que lleva el colchón a 3 meses de gasto en un año (pauta general).
 */
import { monthTotals, prevMonth, round2 } from '@pf/domain';
import type { Computed } from './model.js';
import { categoryInsights, monthlyExpenseAvg, type CategoryInsight } from './insights.js';
import { plannedInMonth } from './planned.js';
import { levelOf } from './year-model.js';
import { cushionCash } from './liquidity-model.js';
import { planned, type PlanRow, type Section } from './planner-model.js';

const MONTHS = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
export const monthName = (i: number) => MONTHS[i]!;

/** Lo de cada categoría en un mes (gastos), calculado una vez por sincronización. */
const cache = new WeakMap<Computed, Map<string, Map<string, CategoryInsight>>>();
function insightOf(c: Computed, month: string, category: string): CategoryInsight | undefined {
  let byMonth = cache.get(c);
  if (!byMonth) { byMonth = new Map(); cache.set(c, byMonth); }
  let m = byMonth.get(month);
  if (!m) { m = new Map(categoryInsights(c, month).map(x => [x.category.toLowerCase(), x])); byMonth.set(month, m); }
  return m.get(category.toLowerCase());
}

export interface RefMonth {
  /** Mes de referencia (AAAA-MM) que ocupa esta posición del calendario, o '' si no hay. */
  month: string;
  /** Lo real (en positivo; null si el mes no está cerrado o no hay datos) y lo que de ello fue pago de previstos. */
  real: number | null; planned: number;
  normal: number | null; note: string; extraordinary: boolean;
}
export interface CategoryAdvice {
  category: string; section: Section;
  /** Qué se usa de referencia: «2026» o «los últimos 12 meses». */
  refLabel: string;
  /** Posición = mes del calendario (0 = enero). */
  months: RefMonth[];
  closed: number; refTotal: number;
  extraordinary: RefMonth[]; withNotes: RefMonth[];
  base: number | null; baseWhy: string;
  avgAll: number | null; lastQuarter: number | null;
  /** Lo de cada mes sin lo pagado de previstos (null sin datos): para copiar el patrón de temporada. */
  pattern: Array<number | null>;
}

/** Meses de referencia para planificar «year»: los cerrados del año anterior; si hay menos de 3, los últimos 12 cerrados. */
export function referenceMonths(c: Computed, year: number, today: string): { months: string[]; label: string } {
  const current = today.slice(0, 7);
  const first = c.months[c.months.length - 1] ?? current;
  const prev = Array.from({ length: 12 }, (_, i) => `${year - 1}-${String(i + 1).padStart(2, '0')}`).filter(m => m < current && m >= first);
  if (prev.length >= 3) return { months: prev, label: String(year - 1) };
  const last: string[] = [];
  for (let m = prevMonth(current); last.length < 12 && m >= first; m = prevMonth(m)) last.unshift(m);
  return { months: last, label: 'los últimos 12 meses' };
}

const mean = (v: number[]) => (v.length ? Math.round(v.reduce((t, x) => t + x, 0) / v.length) : null);

export function categoryAdvice(c: Computed, category: string, section: Section, year: number, today: string): CategoryAdvice {
  const ref = referenceMonths(c, year, today);
  const is = (n: string) => n.toLowerCase() === category.toLowerCase();
  const months: RefMonth[] = Array.from({ length: 12 }, (): RefMonth => ({ month: '', real: null, planned: 0, normal: null, note: '', extraordinary: false }));
  for (const m of ref.months) {
    const i = Number(m.slice(5, 7)) - 1;
    const note = c.wb.notes.find(n => n.month === m && is(n.category))?.note ?? '';
    if (section === 'income') {
      const real = round2(c.items.filter(x => x.month === m && is(x.category)).reduce((t, x) => t + x.attributable, 0));
      months[i] = { month: m, real, planned: 0, normal: null, note, extraordinary: false };
      continue;
    }
    const ins = insightOf(c, m, category);
    const real = ins?.amount ?? 0;
    const pl = section === 'goal' ? 0 : plannedInMonth(c, category, m).amount;
    const level = ins ? levelOf({ amount: round2(real - pl), normal: ins.normal, unusual: ins.unusual }) : '';
    months[i] = { month: m, real, planned: pl, normal: ins?.normal ?? null, note,
      extraordinary: !!ins && (ins.unusual || level === 'dev2' || (!!note && level !== '')) };
  }
  const closed = months.filter(x => x.real !== null);
  const net = (x: RefMonth) => round2((x.real ?? 0) - x.planned);
  const ordinary = closed.filter(x => !x.extraordinary).map(net);
  const extraordinary = closed.filter(x => x.extraordinary);
  const useOrdinary = ordinary.length >= 3;
  const base = useOrdinary ? mean(ordinary) : mean(closed.map(net));
  const last3 = [...closed].sort((a, b) => a.month.localeCompare(b.month)).slice(-3).map(net);
  return {
    category, section, refLabel: ref.label, months, closed: closed.length,
    refTotal: round2(closed.reduce((t, x) => t + (x.real ?? 0), 0)),
    extraordinary, withNotes: closed.filter(x => x.note),
    base, baseWhy: !closed.length ? 'sin datos de referencia'
      : useOrdinary && extraordinary.length ? `lo normal de ${ref.label} sin ${extraordinary.length === 1 ? 'el mes extraordinario' : `los ${extraordinary.length} meses extraordinarios`}${closed.some(x => x.planned) ? ' ni lo pagado de previstos' : ''}`
      : `la media de ${closed.length} ${closed.length === 1 ? 'mes' : 'meses'} de ${ref.label}${closed.some(x => x.planned) ? ' sin lo pagado de previstos' : ''}`,
    avgAll: mean(closed.map(x => x.real ?? 0)), lastQuarter: last3.length ? mean(last3) : null,
    pattern: months.map(x => (x.real === null ? null : Math.round(net(x)))),
  };
}

/* ---------- Cambios en una fila del plan (sin guardar: los guarda el planificador) ---------- */

/** «Nuevo importe desde [mes]»: ese mes y los siguientes; los anteriores no cambian. */
export function applyFrom(r: Pick<PlanRow, 'monthly' | 'overrides'>, from: number, amount: number) {
  for (let i = from; i < 12; i++) r.overrides[i] = r.monthly !== null && amount === r.monthly ? null : amount;
}

/** Base para todo el año (o desde el mes actual, si el año ya ha empezado). */
export function applyBase(r: Pick<PlanRow, 'monthly' | 'overrides'>, amount: number, fromMonth: number) {
  if (fromMonth <= 0) { r.monthly = amount; r.overrides = Array(12).fill(null); return; }
  applyFrom(r, fromMonth, amount);
}

/** El patrón mes a mes del año de referencia (los meses sin datos, con la media). */
export function applyPattern(r: Pick<PlanRow, 'monthly' | 'overrides'>, pattern: Array<number | null>, fromMonth: number) {
  const known = pattern.filter((v): v is number => v !== null);
  if (!known.length) return;
  const avg = Math.round(known.reduce((t, v) => t + v, 0) / known.length);
  if (fromMonth <= 0) r.monthly = avg;
  for (let i = Math.max(0, fromMonth); i < 12; i++) { const v = pattern[i] ?? null; r.overrides[i] = v === null || v === r.monthly ? null : v; }
}

/** Si la fila cambia de importe a partir de un mes (p. ej. subida de sueldo en abril): ese mes; si no, null. */
export function stepFrom(r: Pick<PlanRow, 'monthly' | 'overrides'>): { month: number; amount: number } | null {
  const i = r.overrides.findIndex(o => o !== null);
  if (i <= 0) return null;
  const v = r.overrides[i]!;
  return r.overrides.slice(i).every(o => o === v) && v !== r.monthly ? { month: i, amount: v } : null;
}

/* ---------- El año entero ---------- */

export interface Projection {
  /** Ahorro al cierre: real de los meses cerrados + plan de los que faltan. */
  closing: number; goal: number; realMonths: number;
  /** Ahorro de cada mes (real o planificado) y los meses en rojo del plan. */
  perMonth: number[]; redMonths: number[];
}
export function yearProjection(c: Computed, rows: PlanRow[], year: number, today: string): Projection {
  const current = today.slice(0, 7);
  const sum = (sections: Section[], i: number) => rows.filter(r => sections.includes(r.section)).reduce((t, r) => t + planned(r, i), 0);
  let realMonths = 0;
  const perMonth = Array.from({ length: 12 }, (_, i) => {
    const m = `${year}-${String(i + 1).padStart(2, '0')}`;
    if (m < current) { realMonths++; return round2(monthTotals(c.items, m).savings); }
    return round2(sum(['income'], i) - sum(['fixed', 'variable'], i));
  });
  const goals = Array.from({ length: 12 }, (_, i) => sum(['goal'], i));
  const redMonths = perMonth.map((v, i) => ({ v: round2(v - goals[i]!), i })).filter(x => `${year}-${String(x.i + 1).padStart(2, '0')}` >= current && x.v < -0.5).map(x => x.i);
  return { closing: round2(perMonth.reduce((t, v) => t + v, 0)), goal: round2(goals.reduce((t, v) => t + v, 0)), realMonths, perMonth, redMonths };
}

/** Objetivo de ahorro sugerido al mes: el que llevaría el colchón a 3 meses de gasto en un año. null si ya los cubre o no hay datos. */
export function savingsGoalSuggestion(c: Computed, today: string): { monthly: number; months: number; why: string } | null {
  const avg = monthlyExpenseAvg(c, today.slice(0, 7));
  if (!avg) return null;
  const cash = cushionCash(c, today);
  const months = Math.round(cash / avg * 10) / 10;
  if (months >= 3) return null;
  const monthly = Math.ceil((3 * avg - cash) / 12 / 50) * 50;
  return { monthly, months, why: `para llevar tu colchón de ${months.toLocaleString('es-ES')} a 3 meses de gasto en un año` };
}
